import { useState, useEffect } from 'react'
import loadable from '@loadable/component'
import { Helmet } from "react-helmet-async"
import { arrayMoveImmutable } from 'array-move'

import type {
    TPossibleScreens,
    TUpload,
    TUploadedFile,
    TUploadingError,
    TErrorText,
    TPage,
} from "../types"

import {
    saveThumb,
    loadThumb,
} from '../helpers'

import { baseURL, development, isSnap } from '../constants'

import { statOnLoad } from '../Stat'

import Wrapper from "../elements/Wrapper"
import DefaultContent, { Content } from "../elements/Content"

import {
    Buttons,
    UploadButtons,
    CloudButtons,
    ButtonOneDrive,
    ButtonDropBox,
    ButtonGoogleDrive,

    PagePreviwButtons,
    PagePreviwButton,
} from "../elements/Buttons"

import { convert, localUpload } from "../Services"

import DropZone from "../screens/DropZone"

const ErrorBlock = loadable(() => import('../elements/Errors'))
const PagesPreview = loadable(() => import('../screens/PagesPreview'))
const Preloader = loadable(() => import("../screens/Preloader"))
const Download = loadable(() => import("../screens/Download"))
const Error = loadable(() => import("../screens/Error"))

const uploadedFilesT: TUploadedFile[] = []
const uploadingErrors: TUploadingError[] = []
let downloadLink: string
let defaultPagesLength: number

const RearrangePDF = () => {
    const [currentScreen, switchScreen] = useState<TPossibleScreens>('filespreview')
    const [progress, setProgress] = useState(0)
    const [uploadedFiles, setUploadedFiles] = useState<TUploadedFile[]>(uploadedFilesT)
    const [countUploadingErrors, setCountUploadingErrors] = useState(uploadingErrors.length)
    const [errorText, setErrorText] = useState<TErrorText>({})
    const [showErrorBlock, setShowErrorBlock] = useState(true)
    const [pages, setPages] = useState<TPage[]>([])
    const [disableButton, setDisableButton] = useState(true)

    const [prep, setPrep] = useState('Uploading File')

    const upload: TUpload = {

        action: "rearrange_pdf",
        allowExtantions: ['pdf'],
        convertTo: "pdf",
        maxSize: 25, // MB
        multiselect: false,

        setDownloadLink: (link: string) => downloadLink = link,
        getDownloadLink: () => downloadLink,
        setProgress: (progress) => setProgress(progress),
        getProgress: () => progress,
        setScreen: (screen) => {
            if (window.scrollY > 220) window.scrollTo({ top: 0, behavior: "smooth" })
            if (screen === 'uploading' || screen === 'converting' || screen === 'preparing') setProgress(0)
            return switchScreen(screen)
        },
        getScreen: () => currentScreen,
        addFiles: (uploadedFile) => {
            return setUploadedFiles([...uploadedFile])
        },
        delFile: (uploadedFile) => {
            return setUploadedFiles(uploadedFiles.filter((element) => element !== uploadedFile))
        },
        clearFiles: () => setUploadedFiles([]),
        getFiles: () => uploadedFiles,
        moveFiles: (oldIndex, newIndex) => {
            return setUploadedFiles(arrayMoveImmutable(uploadedFiles, oldIndex, newIndex))
        },
        addError: (uploadingError) => {
            uploadingErrors.push(uploadingError)
            setShowErrorBlock(true)
            return setCountUploadingErrors(countUploadingErrors + 1)
        },
        delError: (uploadingError) => {
            const idx = uploadingErrors.indexOf(uploadingError)
            if (idx !== -1) {
                uploadingErrors.splice(idx, 1)
                return setCountUploadingErrors(countUploadingErrors - 1)
            }
        },
        clearErrors: () => {
            uploadingErrors.splice(0, uploadingErrors.length)
            return setCountUploadingErrors(0)
        },
        hideErrors: () => setShowErrorBlock(false),
        getErrors: () => uploadingErrors,
        setErrorText: setErrorText,

        getPages: () => pages,
        setPages: (newPages) => {
            if (newPages.length != defaultPagesLength ||
                newPages.some((e, i) => {
                    return e.page_num != i || e.rotate % 360 != 0
            })) setDisableButton(false)
            else setDisableButton(true)

            return setPages(newPages)
        },
        setDefaultPages: (pages) => {
            defaultPagesLength = pages.length
            return saveThumb(pages)
        },
    }

    useEffect(() => {
        
        const token = location.hash.replace('#', '')

        if (token.includes("id=")) {
            const docid = token.slice(3);
            if (Number.isInteger(Number(docid)) && String(Number(docid)) == docid) {
                localUpload(upload, docid, true)
                setPrep('Prepairing File')
            }
        }
        else if (token.length === 36) {
            localUpload(upload, token)
            setPrep('Prepairing File')
        }
        else statOnLoad(upload)
    },[])


    // useEffect(() => {
    //     const token = location.hash.replace('#', '')
    //     if (token.length === 36) {
    //         location.hash = ''
    //         localUpload(upload, token)
    //     }
    //     else statOnLoad(upload)
    // }, [])

    return (
        <Wrapper>
            <Helmet>
                <title>Rearrange PDF Pages | Templateroller</title>
                <meta name="keywords" content="rearrange pdf pages, how to rearrange pages in pdf, rearrange pdf, pdf rearrange pages, rearrange pdf pages free, how to rearrange pages in pdf, how to rearrange pdf pages, how to rearrange pages in a pdf, organize pdf pages, organize pdf, how to organize pages in pdf" />
                <meta name="description" content="Looking to organize PDF pages within your files? Delete or rearrange PDF pages using TemplateRoller's free online PDF management tool." />
                <meta property='og:title' content="Rearrange PDF Pages" />
                <meta property='og:description' content="Wondering how to organize pages in PDF files? Use our free online tool to rearrange PDF pages, sort PDF files, and delete specific parts of your document - all free of charge." />
                <meta property='og:url' content={`${baseURL}/tools/rearrange-pdf.html`} />
                <meta property="og:image" content={`${baseURL}/tools/assets/og-rearrange-pdf.png`} />
                <meta property='og:locale' content="en_US" />
                <meta property='twitter:title' content="Rearrange PDF Pages" />
                <meta property='twitter:description' content="Wondering how to organize pages in PDF files? Use our free online tool to rearrange PDF pages, sort PDF files, and delete specific parts of your document - all free of charge." />
                <meta property="twitter:image:src" content={`${baseURL}/tools/assets/og-rearrange-pdf.png`} />
                <link rel='canonical' href={`${baseURL}/tools/rearrange-pdf.html`} />
            </Helmet>

            {
                currentScreen === 'filespreview' &&
                <h1>Rearrange PDF Pages</h1>
            }

            {
                currentScreen === 'filespreview' &&
                (uploadedFiles.length === 0 || pages.length === 0) &&
                <DefaultContent>
                    {
                        countUploadingErrors > 0 &&
                        showErrorBlock &&
                        <ErrorBlock upload={upload} />
                    }

                    <DropZone
                        title="Rearrange PDF online: drag and drop PDF file to manage pages"
                        description="Wondering how to organize pages in PDF files? Use our free online tool to rearrange PDF pages, sort PDF files, and delete specific parts of your document — all free of charge."
                        buttonText="Browse file"
                        warning={`Upload document of up to ${upload.maxSize} MB in ${(upload.allowExtantions.length > 2 ? upload.allowExtantions.map((e, i) => i == upload.allowExtantions.length - 1 ? `and .${e}` : `.${e}`) : upload.allowExtantions.map(e => `.${e}`)).join(', ')} format${upload.allowExtantions.length > 1 ? 's' : ''}`}

                        upload={upload}
                    />
                    <Buttons>
                        <UploadButtons>
                            <CloudButtons>
                                <ButtonDropBox upload={upload} />
                                <ButtonOneDrive upload={upload} />
                                <ButtonGoogleDrive upload={upload} />
                            </CloudButtons>
                        </UploadButtons>
                    </Buttons>

                </DefaultContent>

            }

            {
                currentScreen === 'filespreview' &&
                uploadedFiles.length > 0 &&
                pages.length > 0 &&
                <Content>
                    {
                        countUploadingErrors > 0 &&
                        showErrorBlock &&
                        <ErrorBlock upload={upload} />
                    }

                    <PagesPreview upload={upload} sortable={true} />

                    <Buttons>
                        <PagePreviwButtons>
                            <PagePreviwButton
                                primary={false}
                                onClick={() => {
                                    if (disableButton) {
                                        if (confirm('Do you want to upload again?')) {
                                            setUploadedFiles([])
                                            return setPages([])
                                        }
                                    }
                                    else {
                                        const thumb = loadThumb()
                                        if (confirm('Do you want to discard changes?') && thumb) {
                                            setDisableButton(true)
                                            return setPages(thumb)
                                        }
                                    }
                                }}
                            >{`${disableButton ? 'Reset' : 'Undo'}`}</PagePreviwButton>
                            <PagePreviwButton
                                primary={true}
                                disabled={disableButton}
                                onClick={() => {
                                    return convert({
                                        upload: upload,
                                    })
                                }}
                            >Apply</PagePreviwButton>
                        </PagePreviwButtons>
                    </Buttons>
                </Content>
            }

            {
                currentScreen === 'uploading' &&
                <DefaultContent>
                    <Preloader
                        type="progress"
                        title="Uploading File"
                        description="Don’t close this tab, your files are being uploaded. This shouldn’t take long."
                        progress={progress}
                    />
                </DefaultContent>
            }

            {
                currentScreen === 'preparing' &&
                <DefaultContent>
                    <Preloader
                        type="progress"
                        title="Preparing Pages"
                        description="Don’t close this tab, your files are being prepared. This won't take long."
                        progress={progress}
                    />
                </DefaultContent>
            }

            {
                currentScreen === 'converting' &&
                <DefaultContent>
                    <Preloader
                        type="progress"
                        title="Processing Document"
                        description="Don’t close this tab, your files are being processed. This won't take long."
                        progress={progress}
                    />
                </DefaultContent>
            }

            {
                currentScreen === 'link' &&
                <DefaultContent>
                    <Download
                        title="Rearrange PDF Pages"
                        description="Your file has been rearranged. Click on “Download” to save the file to your device."
                        buttonText="Download"

                        upload={upload}
                        onClick={() => {
                            // @ts-ignore
                            window.stat?.(`tools_result_${upload.action}`)
                        }}
                    />
                </DefaultContent>
            }

            {
                currentScreen === 'error' &&
                <DefaultContent>
                    <Error text={errorText} />
                </DefaultContent>
            }

        </Wrapper>
    )
}

export default RearrangePDF